<template>
  <div class="ns-root">
    <!-- 滾動進度條 -->
    <div class="scroll-progress"></div>

    <!-- Header -->
    <header class="header">
      <div class="wrap bar">
        <div class="brand">
          <NuxtImg v-if="adminInfo.logo"
                   format="webp"
                   loading="lazy"
                   height="50"
                   :src="picUrl + adminInfo.logo"
                   alt="logo"/>
          <span>空間便利店</span>
        </div>
        <nav class="nav">
          <a class="pill" href="#" @click="handelLoginBtn">會員登入</a>
          <NuxtLink class="pill primary" to="/register">註冊</NuxtLink>
        </nav>
      </div>
    </header>

    <!-- Hero -->
    <section class="hero">
      <div class="wrap">
        <div class="banner"
             style="--hero: url('https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=2000&q=85');">
          <img alt="橫幅圖"
               src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=2000&q=85"
               loading="eager"
               referrerpolicy="no-referrer"
               @error="onHeroError($event)"
          />
        </div>
        <div class="copy">
          <span class="eyebrow">{{ adminInfo.brand_name }}</span>
          <h1>場地租借，簡單有品牌感</h1>
          <p>場地挑到好心煩，專人服務真簡單，輕鬆預訂好心安！</p>
        </div>
      </div>
    </section>

    <!-- Social CTA -->
    <section class="social wrap scroll-scale-in" aria-label="社群連結">
      <ul class="slist">
        <li>
          <a class="sbtn fb floating-element" :href="adminInfo.facebook_url" aria-label="Facebook 粉絲專頁" style="animation-delay: 0.1s">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H8.08v-2.9h2.36V9.41c0-2.33 1.39-3.62 3.52-3.62.72 0 1.86.13 2.32.19v2.55h-1.31c-1.29 0-1.69.8-1.69 1.62v1.92h2.87l-.46 2.9h-2.41V22c4.78-.76 8.44-4.92 8.44-9.94Z" fill="#fff"/>
            </svg>
            <span>Facebook 粉專</span>
          </a>
        </li>
        <li>
          <a class="sbtn line floating-element" :href="adminInfo.line" aria-label="LINE 官方" style="animation-delay: 0.3s">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M20.5 10.8c0-3.9-3.9-7-8.7-7S3 6.9 3 10.8c0 3.6 3.3 6.6 7.7 6.9.3 0 .6.2.6.5l-.1 1.7c0 .2.2.4.4.3 2-.9 3.9-2.2 5.4-3.8 1.9-1.5 3.1-3.6 3.1-5.6Z" fill="#fff"/>
            </svg>
            <span>LINE 官方</span>
          </a>
        </li>
        <li>
          <a class="sbtn ig floating-element" :href="adminInfo.instagram" aria-label="Instagram" style="animation-delay: 0.5s">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="2" y="2" width="20" height="20" rx="5" fill="#fff"/>
            </svg>
            <span>Instagram</span>
          </a>
        </li>
      </ul>
    </section>

    <!-- 特色區 -->
    <section class="features wrap" aria-label="特色介紹">
      <div class="feature scroll-fade-in">
        <div class="f-text scroll-slide-left">
          <h3>
            <span>打造您的完美活動空間</span>
            <i class="bar" aria-hidden="true"></i>
          </h3>
          <p>
            在我們精心挑選的優質場地中，感受空間的無限可能，探索您活動的理想選擇。無論是商務會議、婚宴慶典、展覽活動或私人聚會，我們提供多樣化的場地選項，滿足您的各種需求。
          </p>
        </div>
        <div class="f-media scroll-slide-right">
          <img src="https://images.unsplash.com/photo-1518600506278-4e8ef466b810?auto=format&fit=crop&w=1600&q=85"
               alt="現代會議廳場地（無人物）"
               loading="lazy"
               referrerpolicy="no-referrer"
               @error="onFeatureError($event)"
          />
        </div>
      </div>
      <div class="feature reverse scroll-fade-in">
        <div class="f-text scroll-slide-right">
          <h3>
            <span>您的活動，我們的專業</span>
            <i class="bar" aria-hidden="true"></i>
          </h3>
          <p>
            精選優質場地，專業貼心服務。讓每一場活動都在理想的空間中完美呈現，創造屬於您的精彩時刻。
          </p>
        </div>
        <div class="f-media scroll-slide-left">
          <img src="https://images.unsplash.com/photo-1504805572947-34fad45aed93?auto=format&fit=crop&w=1600&q=85"
               alt="大型會議廳座位與舞台（無人物）"
               loading="lazy"
               referrerpolicy="no-referrer"
               @error="onFeatureError($event)"
          />
        </div>
      </div>
    </section>

    <!-- Cards：空間列表 -->
    <main class="wrap">
      <section class="promo-wide"
               style="--img: url('https://images.unsplash.com/photo-1540574163026-643ea20ade25?auto=format&fit=crop&w=2000&q=85');">
        <div class="content wrap">
          <h2>讓每個活動都成為難忘回憶</h2>
          <p>
            在我們嚴選認證的頂級場地空間裡，感受專業服務的貼心，探索活動舉辦的無限創意。從典雅宴會廳到現代會議室，從戶外花園到展演空間，為您的重要時刻提供完美舞台。
          </p>
        </div>
      </section>

      <section class="section" id="spaces">
        <h2>空間列表</h2>
        <div class="cards">
          <template v-if="spaces.length > 0">
            <NewSpaceSpaceCard v-for="item in spaces" :key="item.space_id" :space="item" />
          </template>
          <template v-else>
            <div class="body scroll-fade-in" style="text-align: center; color: var(--text-muted)">
              目前沒有可顯示的空間
            </div>
          </template>
        </div>
      </section>
    </main>

    <!-- Contact Section -->
    <section class="contact-section">
      <div class="wrap">
        <div class="section-header scroll-fade-in">
          <h2>聯絡我們</h2>
        </div>
        <div class="contact-grid">
          <div class="contact-map scroll-slide-left">
            <div class="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3615.0024!2d121.5654!3d25.0330!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442abb6da9c9e1f%3A0x1206bcf082fd10a6!2z5Y-w5YyX5biC5L-h576p5Y2A5L-h576p6LevNOaute!5e0!3m2!1szh-TW!2stw!4v1700000000000!5m2!1szh-TW!2stw"
                width="100%"
                height="300"
                title="空間便利店位置地圖"
              >
              </iframe>
            </div>
          </div>
          <div class="contact-info scroll-slide-right">
            <div class="contact-item scroll-scale-in floating-element" style="animation-delay: 0.1s">
              <h4>公司地址</h4>
              <p>台北市信義區信義路5段7號</p>
            </div>
            <div class="contact-item scroll-scale-in floating-element" style="animation-delay: 0.2s">
              <h4>聯絡 / 預約</h4>
              <p><a href="tel:02-8101-8800">{{ adminInfo.phone }}</a></p>
            </div>
            <div class="contact-item scroll-scale-in floating-element" style="animation-delay: 0.3s">
              <h4>電子郵件</h4>
              <p><a :href="`mailto:${adminInfo.email}`">{{ adminInfo.email }}</a></p>
            </div>
            <div class="contact-item scroll-scale-in floating-element" style="animation-delay: 0.4s">
              <h4>營業時間</h4>
              <p></p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="brand-footer">
      <div class="wrap">
        <div class="foot-grid">
          <div class="col">
            <h4>空間便利店</h4>
            <p>如有任何產品、課程相關問題歡迎與我們聯繫</p>

            <div class="sicons" aria-label="社群圖示">
              <a href="#" aria-label="Instagram"></a>
              <a href="#" aria-label="Messenger"></a>
              <a href="#" aria-label="LINE"></a>
            </div>
          </div>
          <div class="col">
            <h4>聯絡方式</h4>
            <ul class="contact">
              <li>IG：@cc_circuscorner ・ @cc_pilatescorner</li>
              <li>LINE ID：@900vjplm</li>
              <li>E-mail：hello@onestep.place</li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  </div>
  
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect, watch, nextTick } from 'vue'
import { spaceListByAdminSSR } from '@/api/space-ssr'
import { getAdminInfoSSR } from '@/api/website-ssr'
import NewSpaceSpaceCard from '@/components/NewSpace/SpaceCard.vue'
import { useGoTo } from '@/composables/useGoTo'

const props = defineProps<{ adminId: number }>()

const { handelLoginBtn } = useGoTo()

const spaces = ref<any[]>([])
const adminInfo = ref<Record<string, any>>({})
const picUrl = import.meta.env.VITE_APP_BASE_IMG_URL

function onHeroError(e: Event) {
  const target = (e as any)?.target as HTMLImageElement | undefined
  if (!target) return
  target.onerror = null
  target.src = 'https://picsum.photos/2000/900?random=113'
}
function onFeatureError(e: Event) {
  const target = (e as any)?.target as HTMLImageElement | undefined
  if (!target) return
  target.onerror = null
  target.src = 'https://picsum.photos/1600/900?random=31'
}

// 依 adminId 取得資料
const { data: spaceListData, error: spaceErr } = await useAsyncData(
  `newSpaceList:${props.adminId}`,
  async () => {
    const queryData = {
      request_id: '',
      admin_id: props.adminId,
      pagination: { dir: 1, limit: 200, page: 1, sort: 'created_date' },
    }
    try {
      const res = await spaceListByAdminSSR(queryData)
      return res
    } catch (e) {
      console.error('space-list SSR 失敗', e)
      console.error('useAsyncData newSpaceList error', spaceErr)
      return { list: [], total: 0 }
    }
  },
  { watch: [() => props.adminId] }
)

const { data: adminInfoData, error: adminInfoError } = await useAsyncData(
  `adminInfo:${props.adminId}`,
  async () => {
    try {
      return await getAdminInfoSSR({ admin_id: props.adminId })
    } catch (e) {
      console.error('getAdminInfo SSR 失敗', e)
      console.error('useAsyncData adminInfo error', adminInfoError)
      return {}
    }
  },
  { watch: [() => props.adminId] }
)

watchEffect(() => {
  if (spaceListData.value && Array.isArray(spaceListData.value.list)) {
    spaces.value = spaceListData.value.list
  }
  if (adminInfoData.value && typeof adminInfoData.value === 'object') {
    adminInfo.value = adminInfoData.value as any
  }
})

// 滾動進度條 + 進場動畫
// IntersectionObserver 實例（供後續新增節點重新 observe）
const ioRef = ref<IntersectionObserver | null>(null)

function observeAnimatedElements() {
  const selectors = [
    '.scroll-fade-in',
    '.scroll-slide-left',
    '.scroll-slide-right',
    '.scroll-scale-in',
    '.scroll-rotate-in',
    '.scroll-stagger',
  ].join(',')
  const els = document.querySelectorAll(selectors)
  els.forEach((el) => ioRef.value?.observe(el))
  // 立即將視窗內的元素標記為可見，避免初次載入為透明
  els.forEach((el) => {
    const rect = (el as HTMLElement).getBoundingClientRect()
    if (rect.top < window.innerHeight * 0.9 && rect.bottom > 0) {
      (el as HTMLElement).classList.add('visible')
    }
  })
}

onMounted(() => {
  const progressBar = document.querySelector('.scroll-progress') as HTMLDivElement | null
  const updateProgress = () => {
    const scrollTop = window.pageYOffset
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const percent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
    if (progressBar) progressBar.style.width = percent + '%'
  }
  updateProgress()
  window.addEventListener('scroll', updateProgress, { passive: true })

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
        }
      })
    },
    { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
  )
  ioRef.value = observer
  observeAnimatedElements()
})

// 當空間列表資料變動時，重新 observe 新增的節點
watch(spaces, async () => {
  await nextTick()
  observeAnimatedElements()
})
</script>

<style scoped lang="scss">
/* 以下樣式取自原頁面，保持視覺一致性 */
.ns-root {
  --primary: #c9a876;
  --accent: #a08660;
  --dark: #2c2c2c;
  --dark-light: #404040;
  --page: #ffffff;
  --bg: #ffffff;
  --surface: #fafafa;
  --text: #2c2c2c;
  --text-muted: #666666;
  --text-light: #999999;
  --border: #e5e5e5;
  --border-light: #f0f0f0;
  --radius: 8px;
  --shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  --shadow-hover: 0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
  --max: 1200px;
  --s2: 8px;
  --s3: 12px;
  --s4: 16px;
  --s5: 20px;
  --s6: 28px;
  --s8: 48px;
  --s10: 64px;

  min-height: 100vh;
  margin: 0;
  background: var(--page);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang TC', 'Noto Sans TC',
    'Segoe UI', Roboto, Helvetica, Arial;
  line-height: 1.7;
  scroll-behavior: smooth;
}

.ns-root * { box-sizing: border-box; }
.ns-root img { max-width: 100%; height: auto; display: block; }
.ns-root a { text-decoration: none; color: inherit; }
.ns-root h1, .ns-root h2 { line-height: 1.2; margin: 0 0 var(--s4); }
.ns-root h1 { font-size: clamp(2rem, 4.2vw, 3rem); }
.ns-root h2 { font-size: clamp(1.3rem, 2vw, 1.6rem); }
.wrap { max-width: var(--max); margin-inline: auto; padding-inline: clamp(16px, 3vw, 28px); }

.scroll-progress { position: fixed; top: 0; left: 0; width: 0%; height: 3px; background: var(--primary); z-index: 9999; transition: width 0.1s ease; box-shadow: 0 2px 4px rgba(201, 168, 118, 0.3); }

.header { position: sticky; top: 0; z-index: 40; background: rgba(255,255,255,0.98); backdrop-filter: blur(20px); border-bottom: 1px solid var(--border-light); transition: all 0.3s ease; }
.header.scrolled { box-shadow: var(--shadow); }
.bar { height: 80px; display: flex; align-items: center; gap: 12px; }
.brand { display: flex; align-items: center; gap: 16px; font-weight: 700; color: var(--text); font-size: 1.1rem; }
.nav { margin-left: auto; display: flex; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
.pill { display: inline-flex; align-items: center; gap: 8px; padding: 0.6rem 1.2rem; border-radius: var(--radius); border: 1px solid var(--border); background: var(--bg); font-weight: 600; color: var(--text); transition: all 0.3s ease; }
.pill:hover { border-color: var(--primary); transform: translateY(-1px); box-shadow: var(--shadow); }
.pill.primary { border-color: transparent; background: var(--primary); color: #fff; box-shadow: var(--shadow); }
.pill.primary:hover { transform: translateY(-2px); box-shadow: var(--shadow-hover); }

.hero { position: relative; }
.hero .wrap { position: relative; }
.banner { position: relative; width: 100vw; margin-left: calc(50% - 50vw); border-radius: 0; overflow: hidden; box-shadow: var(--shadow); min-height: clamp(500px, 70vh, 800px); }
.banner img { width: 100%; height: clamp(500px, 70vh, 800px); object-fit: cover; }
.banner::before { content: ''; position: absolute; inset: 0; background-image: var(--hero); background-size: cover; background-position: center; z-index: 0; }
.banner::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(44,44,44,0.8) 0%, rgba(44,44,44,0.4) 45%, rgba(44,44,44,0.1) 72%), linear-gradient(180deg, rgba(0,0,0,0.2), rgba(0,0,0,0)); z-index: 1; }
.copy { position: absolute; left: clamp(18px, 6vw, 88px); bottom: clamp(22px, 12vh, 160px); z-index: 2; color: #fff; }
.copy .eyebrow { display: inline-block; margin-bottom: 12px; padding: 10px 20px; border-radius: var(--radius); background: var(--primary); color: #fff; font-weight: 700; box-shadow: var(--shadow); font-size: 0.9rem; }
.copy h1 { font-size: clamp(2.5rem, 5vw, 4rem); font-weight: 700; margin-bottom: var(--s4); }
.copy p { opacity: 0.95; margin-top: var(--s4); font-size: 1.1rem; line-height: 1.6; }

.social { margin: var(--s8) 0; padding: var(--s6) 0; background: var(--surface); }
.slist { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; list-style: none; margin: 0; padding: 0; }
.sbtn { display: inline-flex; align-items: center; gap: 12px; font-weight: 600; font-size: 1rem; padding: 1rem 1.5rem; border-radius: var(--radius); box-shadow: var(--shadow); transition: all 0.3s ease; border: 1px solid var(--border); background: var(--bg); cursor: pointer; }
.sbtn svg { width: 20px; height: 20px; }
.sbtn:hover { transform: translateY(-2px); box-shadow: var(--shadow-hover); border-color: var(--primary); }
.sbtn.fb, .sbtn.line, .sbtn.ig { background: var(--primary); color: #fff; border-color: transparent; }

.features { margin-block: var(--s10); padding: var(--s8) 0; }
.feature { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s8); align-items: center; margin-bottom: var(--s10); }
.feature .f-text h3 { font-size: clamp(1.8rem, 3.5vw, 2.5rem); margin: 0 0 var(--s4); font-weight: 700; color: var(--text); position: relative; }
.feature .f-text h3::after { content: ''; position: absolute; bottom: -8px; left: 0; width: 60px; height: 3px; background: var(--primary); border-radius: 2px; }
.feature .f-text p { max-width: 60ch; color: var(--text-muted); margin: 0; font-size: 1.1rem; line-height: 1.8; }
.feature .f-media img { width: 100%; height: 400px; object-fit: cover; border-radius: var(--radius); box-shadow: var(--shadow); border: 1px solid var(--border-light); transition: all 0.3s ease; }
.feature .f-media img:hover { transform: scale(1.02); box-shadow: var(--shadow-hover); }
.feature.reverse .f-media { order: -1; }
@media (max-width: 900px) {
  .feature { grid-template-columns: 1fr; gap: var(--s6); }
  .feature .f-media img { height: 300px; }
}

.section { margin-block: var(--s10); padding: var(--s8) 0; }
.section h2 { text-align: center; font-size: clamp(2rem, 3.5vw, 2.8rem); font-weight: 700; color: var(--text); margin-bottom: var(--s8); position: relative; }
.section h2::after { content: ''; position: absolute; bottom: -12px; left: 50%; transform: translateX(-50%); width: 80px; height: 3px; background: var(--primary); border-radius: 2px; }
.cards { display: grid; gap: var(--s8); grid-template-columns: 1fr; max-width: 1200px; margin-inline: auto; }
.media { min-height: 320px; border-radius: var(--radius); overflow: hidden; background: var(--surface); border: 1px solid var(--border-light); transition: all 0.3s ease; }
.media:hover { transform: scale(1.02); border-color: var(--primary); }
.media img { width: 100%; height: 100%; object-fit: cover; }
.body { display: flex; flex-direction: column; gap: var(--s4); }
.title { font-weight: 700; letter-spacing: 0.1px; color: var(--text); font-size: 1.2rem; }
.meta { font-size: 0.95rem; color: var(--text-muted); line-height: 1.5; }
.row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin: var(--s3) 0; }
.badge { display: inline-flex; align-items: center; gap: 6px; font-size: 0.88rem; padding: 8px 12px; border-radius: var(--radius); background: rgba(201,168,118,0.1); color: var(--primary); border: 1px solid rgba(201,168,118,0.25); font-weight: 600; }
.pill.price { background: var(--primary); color: #fff; border: 0; font-weight: 700; box-shadow: var(--shadow); }
.pill.price:hover { transform: scale(1.05); box-shadow: var(--shadow-hover); }
.cta { margin-top: var(--s4); }
.cta a { display: inline-flex; align-items: center; gap: 10px; padding: 0.8rem 1.2rem; border-radius: var(--radius); background: var(--primary); color: #fff; font-weight: 700; transition: all 0.3s ease; box-shadow: var(--shadow); text-decoration: none; }
.cta a:hover { transform: translateY(-2px); box-shadow: var(--shadow-hover); }
.cta a::after { content: '→'; margin-left: 4px; font-weight: bold; }

.contact-section { margin-block: var(--s8); padding: var(--s8) 0; background: var(--surface); position: relative; }
.contact-section .section-header { text-align: center; margin-bottom: var(--s8); }
.contact-section h2 { font-size: clamp(2rem, 3.5vw, 2.8rem); font-weight: 700; color: var(--text); margin: 0; position: relative; }
.contact-section h2::after { content: ''; position: absolute; bottom: -12px; left: 50%; transform: translateX(-50%); width: 80px; height: 3px; background: var(--primary); border-radius: 2px; }
.contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s8); align-items: start; }
.map-container { border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow); border: 1px solid var(--border-light); transition: all 0.3s ease; }
.map-container:hover { box-shadow: var(--shadow-hover); transform: translateY(-2px); }
.contact-info { display: grid; gap: var(--s5); }
.contact-item { background: var(--bg); padding: var(--s5); border-radius: var(--radius); box-shadow: var(--shadow); border: 1px solid var(--border-light); transition: all 0.3s ease; }
.contact-item:hover { transform: translateY(-2px); box-shadow: var(--shadow-hover); border-color: var(--primary); }
.contact-item h4 { color: var(--primary); font-weight: 700; margin: 0 0 var(--s3); font-size: 1.1rem; }
.contact-item p { color: var(--text-muted); margin: 0; line-height: 1.6; }
.contact-item a { color: var(--primary); text-decoration: none; font-weight: 600; transition: color 0.3s ease; }
.contact-item a:hover { color: var(--accent); }
@media (max-width: 900px) {
  .contact-grid { grid-template-columns: 1fr; gap: var(--s6); }
}

.brand-footer { background: var(--dark); color: #fff; padding: var(--s8) 0 var(--s5); }
.brand-footer .foot-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: start; }
.brand-footer h4 { margin: 0 0 var(--s4); font-size: 1.2rem; font-weight: 700; color: var(--primary); }
.brand-footer p, .brand-footer li { color: rgba(255,255,255,0.8); margin: 0.4rem 0; line-height: 1.6; }
.brand-footer .sicons { display: flex; gap: 12px; margin-top: var(--s4); }
.brand-footer .sicons a { width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; border: 2px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); transition: all 0.3s ease; }
.brand-footer .sicons a:hover { border-color: var(--primary); background: var(--primary); transform: translateY(-2px); }
.brand-footer .sicons svg { width: 18px; height: 18px; fill: #fff; }
.brand-footer .contact { list-style: none; margin: 0; padding: 0; }
@media (max-width: 900px) { .brand-footer .foot-grid { grid-template-columns: 1fr; gap: 24px; } }

/* 滾動動畫 */
.scroll-fade-in { opacity: 0; transform: translateY(30px); transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
.scroll-fade-in.visible { opacity: 1; transform: translateY(0); }
.scroll-slide-left { opacity: 0; transform: translateX(-12.5px); transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
.scroll-slide-left.visible { opacity: 1; transform: translateX(0); }
.scroll-slide-right { opacity: 0; transform: translateX(12.5px); transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
.scroll-slide-right.visible { opacity: 1; transform: translateX(0); }
.scroll-scale-in { opacity: 0; transform: scale(0.8); transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
.scroll-scale-in.visible { opacity: 1; transform: scale(1); }
.card.scroll-stagger { opacity: 0; transform: translateY(40px); transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
.card.scroll-stagger.visible { opacity: 1; transform: translateY(0); }

@keyframes float-gentle { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
.floating-element { animation: float-gentle 3s ease-in-out infinite; }
.parallax-image { transform: translateY(0); transition: transform 0.1s ease-out; }
</style>
